import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { logWithTimestamp } from "../utils/dateHelpers";

/**
 * 🎨 APPSETTINGSSTORE - UI PREFERENCES & APP CONFIGURATION
 * 
 * 🎯 EXTRACTED from settingsStore (Task 6)
 * Separates UI/UX preferences and app configuration from account management
 * using Zustand with persistence for clean architecture.
 * 
 * 🧠 FEATURES:
 * - Theme and appearance settings (dark mode, currency display)
 * - Localization preferences (language, date format, currency)
 * - User experience settings (notifications, auto-save)
 * - Balance calculation preferences (start date, monthly transfers)
 * - Validation and default values for all settings
 * - Persistence with Zustand middleware
 * 
 * ⚙️ INTEGRATION:
 * - Maintains same API surface as settingsStore app settings
 * - Clean separation from account logic (now in accountsStore)
 * - Ready for localization and theming system
 * - Optimized for UI/UX preference management
 * 
 * 🐛 ERROR HANDLING:
 * - Validation for setting values
 * - Fallback to defaults for invalid settings
 * - Type-safe updates with TypeScript
 * - Reset functionality for troubleshooting
 */

/**
 * Supported date formats for the application
 */
export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

/**
 * Supported currency codes
 */
export type CurrencyCode = 'RON' | 'EUR' | 'USD' | 'GBP';

/**
 * Supported theme modes
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Application settings interface for UI preferences
 */
export interface AppSettings {
  // Core currency and localization
  defaultCurrency: CurrencyCode;
  dateFormat: DateFormat;
  
  // Feature toggles
  enableMultiAccount: boolean;
  enableNotifications: boolean;
  
  // Appearance
  themeMode: ThemeMode; // Enhanced from boolean darkMode
  
  // Performance and UX
  autoSaveInterval: number; // milliseconds
  
  // Balance calculation settings
  balanceStartDate?: string; // Data de început pentru calculul soldului în format ISO (YYYY-MM-DD)
  enableMonthlyTransfers?: boolean; // Transfer automat între luni
  
  // UI preferences
  compactMode?: boolean; // Compact UI layout
  showBalanceInTitle?: boolean; // Show balance in page title
  confirmDeleteActions?: boolean; // Require confirmation for delete actions
}

/**
 * App settings validation error types
 */
export type AppSettingsValidationError = 
  | 'INVALID_CURRENCY' 
  | 'INVALID_DATE_FORMAT' 
  | 'INVALID_AUTO_SAVE_INTERVAL'
  | 'INVALID_BALANCE_START_DATE'
  | 'INVALID_THEME_MODE';

/**
 * State management interface for AppSettingsStore
 */
interface AppSettingsState {
  // Core settings data
  appSettings: AppSettings;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Settings management actions
  updateAppSettings: (settings: Partial<AppSettings>) => void;
  resetAppSettings: () => void;

  // Specific setting actions for commonly used operations
  setThemeMode: (mode: ThemeMode) => void;
  setDefaultCurrency: (currency: CurrencyCode) => void;
  setDateFormat: (format: DateFormat) => void;
  toggleNotifications: () => void;
  toggleMultiAccount: () => void;

  // Balance calculation specific actions
  setBalanceStartDate: (date: string) => void;
  getBalanceStartDate: () => string | null;
  hasBalanceStartDate: () => boolean;
  clearBalanceStartDate: () => void;

  // Utility actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Validation helpers
  validateAppSettings: (settings: Partial<AppSettings>) => AppSettingsValidationError | null;
  isValidCurrency: (currency: string) => currency is CurrencyCode;
  isValidDateFormat: (format: string) => format is DateFormat;
  isValidThemeMode: (mode: string) => mode is ThemeMode;

  // Computed getters
  getCurrentTheme: () => ThemeMode;
  getFormattedCurrency: () => string;
  isCompactModeEnabled: () => boolean;
  getAutoSaveIntervalSeconds: () => number;
}

/**
 * Default application settings
 */
const defaultAppSettings: AppSettings = {
  // Core currency and localization
  defaultCurrency: 'RON',
  dateFormat: 'DD/MM/YYYY',
  
  // Feature toggles
  enableMultiAccount: false,
  enableNotifications: true,
  
  // Appearance
  themeMode: 'system', // Defaults to system preference
  
  // Performance and UX
  autoSaveInterval: 30000, // 30 seconds
  
  // Balance calculation settings - default la începutul lunii curente
  balanceStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString().split('T')[0], // Primul din luna curentă în format YYYY-MM-DD
  enableMonthlyTransfers: false,
  
  // UI preferences
  compactMode: false,
  showBalanceInTitle: false,
  confirmDeleteActions: true,
};

/**
 * Valid currency codes for validation
 */
const VALID_CURRENCIES: CurrencyCode[] = ['RON', 'EUR', 'USD', 'GBP'];

/**
 * Valid date formats for validation
 */
const VALID_DATE_FORMATS: DateFormat[] = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];

/**
 * Valid theme modes for validation
 */
const VALID_THEME_MODES: ThemeMode[] = ['light', 'dark', 'system'];

/**
 * AppSettingsStore implementation using Zustand with persistence
 */
export const useAppSettingsStore = create<AppSettingsState>()(
  persist(
    (set, get) => ({
      // Initial state
      appSettings: defaultAppSettings,
      isLoading: false,
      error: null,

      // Main settings management actions
      updateAppSettings: (settings) => {
        // Validation before update
        const validationError = get().validateAppSettings(settings);
        if (validationError) {
          const errorMessage = `Invalid app settings: ${validationError}`;
          set({ error: errorMessage });
          console.error('[AppSettingsStore] updateAppSettings validation failed:', validationError);
          return;
        }

        set((state) => ({
          appSettings: { ...state.appSettings, ...settings },
          error: null,
        }));

        logWithTimestamp(`AppSettingsStore updated: ${Object.keys(settings).join(', ')}`);
      },

      resetAppSettings: () => {
        set({
          appSettings: { ...defaultAppSettings },
          error: null,
        });
        logWithTimestamp('AppSettingsStore reset to default settings');
      },

      // Specific setting actions
      setThemeMode: (mode) => {
        if (!get().isValidThemeMode(mode)) {
          set({ error: `Invalid theme mode: ${mode}` });
          return;
        }

        set((state) => ({
          appSettings: { ...state.appSettings, themeMode: mode },
          error: null,
        }));
      },

      setDefaultCurrency: (currency) => {
        if (!get().isValidCurrency(currency)) {
          set({ error: `Invalid currency: ${currency}` });
          return;
        }

        set((state) => ({
          appSettings: { ...state.appSettings, defaultCurrency: currency },
          error: null,
        }));
      },

      setDateFormat: (format) => {
        if (!get().isValidDateFormat(format)) {
          set({ error: `Invalid date format: ${format}` });
          return;
        }

        set((state) => ({
          appSettings: { ...state.appSettings, dateFormat: format },
          error: null,
        }));
      },

      toggleNotifications: () => {
        set((state) => ({
          appSettings: { 
            ...state.appSettings, 
            enableNotifications: !state.appSettings.enableNotifications 
          },
          error: null,
        }));
      },

      toggleMultiAccount: () => {
        set((state) => ({
          appSettings: { 
            ...state.appSettings, 
            enableMultiAccount: !state.appSettings.enableMultiAccount 
          },
          error: null,
        }));
      },

      // Balance calculation specific actions
      setBalanceStartDate: (date) => {
        // Validate date format (should be YYYY-MM-DD)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(date)) {
          set({ error: 'Invalid date format. Expected YYYY-MM-DD' });
          return;
        }

        // Validate that it's a valid date
        const parsedDate = new Date(date);
        if (isNaN(parsedDate.getTime())) {
          set({ error: 'Invalid date value' });
          return;
        }

        set((state) => ({
          appSettings: { ...state.appSettings, balanceStartDate: date },
          error: null,
        }));
      },

      getBalanceStartDate: () => {
        return get().appSettings.balanceStartDate || null;
      },

      hasBalanceStartDate: () => {
        return !!get().appSettings.balanceStartDate;
      },

      clearBalanceStartDate: () => {
        set((state) => ({
          appSettings: { ...state.appSettings, balanceStartDate: undefined },
          error: null,
        }));
      },

      // Utility actions
      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setError: (error) => {
        set({ error });
      },

      // Validation helpers
      validateAppSettings: (settings) => {
        // Currency validation
        if (settings.defaultCurrency && !VALID_CURRENCIES.includes(settings.defaultCurrency as CurrencyCode)) {
          return 'INVALID_CURRENCY';
        }

        // Date format validation
        if (settings.dateFormat && !VALID_DATE_FORMATS.includes(settings.dateFormat as DateFormat)) {
          return 'INVALID_DATE_FORMAT';
        }

        // Theme mode validation
        if (settings.themeMode && !VALID_THEME_MODES.includes(settings.themeMode as ThemeMode)) {
          return 'INVALID_THEME_MODE';
        }

        // Auto save interval validation
        if (settings.autoSaveInterval !== undefined) {
          if (typeof settings.autoSaveInterval !== 'number' || 
              settings.autoSaveInterval < 5000 || 
              settings.autoSaveInterval > 300000) { // 5 seconds to 5 minutes
            return 'INVALID_AUTO_SAVE_INTERVAL';
          }
        }

        // Balance start date validation
        if (settings.balanceStartDate !== undefined) {
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(settings.balanceStartDate)) {
            return 'INVALID_BALANCE_START_DATE';
          }

          const parsedDate = new Date(settings.balanceStartDate);
          if (isNaN(parsedDate.getTime())) {
            return 'INVALID_BALANCE_START_DATE';
          }
        }

        return null;
      },

      isValidCurrency: (currency): currency is CurrencyCode => {
        return VALID_CURRENCIES.includes(currency as CurrencyCode);
      },

      isValidDateFormat: (format): format is DateFormat => {
        return VALID_DATE_FORMATS.includes(format as DateFormat);
      },

      isValidThemeMode: (mode): mode is ThemeMode => {
        return VALID_THEME_MODES.includes(mode as ThemeMode);
      },

      // Computed getters
      getCurrentTheme: () => {
        const { themeMode } = get().appSettings;
        
        if (themeMode === 'system') {
          // Detect system preference
          return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        
        return themeMode;
      },

      getFormattedCurrency: () => {
        const { defaultCurrency } = get().appSettings;
        
        const currencySymbols: Record<CurrencyCode, string> = {
          RON: 'lei',
          EUR: '€',
          USD: '$',
          GBP: '£'
        };
        
        return currencySymbols[defaultCurrency] || defaultCurrency;
      },

      isCompactModeEnabled: () => {
        return get().appSettings.compactMode || false;
      },

      getAutoSaveIntervalSeconds: () => {
        return Math.floor(get().appSettings.autoSaveInterval / 1000);
      },
    }),
    {
      name: 'app-settings-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        appSettings: state.appSettings,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Validate settings after rehydration and reset invalid ones
          const validationError = state.validateAppSettings(state.appSettings);
          if (validationError) {
            console.warn('[AppSettingsStore] Invalid settings detected during rehydration, resetting to defaults');
            state.resetAppSettings();
          }
          
          logWithTimestamp('AppSettingsStore rehydrated from localStorage');
        }
      },
    }
  )
);

// Export individual selectors for performance
export const useAppSettings = () => useAppSettingsStore((state) => state.appSettings);
export const useThemeMode = () => useAppSettingsStore((state) => state.getCurrentTheme());
export const useDefaultCurrency = () => useAppSettingsStore((state) => state.appSettings.defaultCurrency);
export const useDateFormat = () => useAppSettingsStore((state) => state.appSettings.dateFormat);
export const useNotificationsEnabled = () => useAppSettingsStore((state) => state.appSettings.enableNotifications);
export const useMultiAccountEnabled = () => useAppSettingsStore((state) => state.appSettings.enableMultiAccount);
export const useCompactMode = () => useAppSettingsStore((state) => state.isCompactModeEnabled());
export const useAppSettingsLoading = () => useAppSettingsStore((state) => state.isLoading);
export const useAppSettingsError = () => useAppSettingsStore((state) => state.error); 